'use client';

import { Element } from '@/data/elements';
import { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { naturalFormsData } from '@/data/natural-forms-data';
import { useTranslations } from 'next-intl';

function spriteStyle(atomicNumber: number): React.CSSProperties {
  const form = naturalFormsData[atomicNumber];
  const s = form?.sprites?.[0];
  if (!s) return {};
  const xPct = s.cols > 1 ? (s.col / (s.cols - 1)) * 100 : 0;
  const yPct = s.rows > 1 ? (s.row / (s.rows - 1)) * 100 : 0;
  return {
    backgroundImage: `url(/minerals/${s.sheet}.png)`,
    backgroundSize: `${s.cols * 100}% ${s.rows * 100}%`,
    backgroundPosition: `${xPct}% ${yPct}%`,
    backgroundRepeat: 'no-repeat',
  };
}

interface PeriodicTableProps {
  elements: Element[];
  onElementClick: (element: Element) => void;
}

export function PeriodicTable({ elements, onElementClick }: PeriodicTableProps) {
  const [hoveredElement, setHoveredElement] = useState<number | null>(null);
  const { theme } = useTheme();
  const t = useTranslations('PeriodicTable');
  const tEl = useTranslations('Element');

  // Séparer les lanthanides et actinides
  const mainElements = elements.filter(el => el.period <= 7);
  const lanthanides = elements.filter(el => el.atomicNumber >= 57 && el.atomicNumber <= 71);
  const actinides = elements.filter(el => el.atomicNumber >= 89 && el.atomicNumber <= 103);

  return (
    <div className="w-full">
      {/* Hint de scroll sur mobile */}
      <p className="lg:hidden text-xs text-[#a8b2d1] text-center mb-2 flex items-center justify-center gap-1">
        <span>←</span> {t('scrollHint')} <span>→</span>
      </p>

      {/* Wrapper scrollable sur mobile */}
      <div className="overflow-x-auto pb-2">
      <div className="min-w-[1100px]">

      {/* Tableau principal */}
      <div
        className="grid gap-1 mb-4"
        style={{
          gridTemplateColumns: 'repeat(18, minmax(70px, 1fr))',
          gridTemplateRows: 'repeat(7, minmax(70px, auto))',
        }}
      >
        {mainElements.map((element) => {
          // Pour les lanthanides et actinides, afficher juste un placeholder
          if (element.atomicNumber === 57 || element.atomicNumber === 89) {
            return (
              <div
                key={element.id}
                style={{
                  gridColumn: element.gridColumn,
                  gridRow: element.gridRow,
                }}
                className="flex items-center justify-center dark:bg-[#112240]/50 light:bg-gray-100 rounded border dark:border-[#233554] light:border-gray-300 dark:text-[#64ffda] light:text-teal-600 text-xs font-semibold cursor-pointer dark:hover:border-[#64ffda] light:hover:border-teal-600 transition-colors"
                onClick={() => {
                  // Scroll vers la section lanthanides/actinides
                  document.getElementById(element.atomicNumber === 57 ? 'lanthanides' : 'actinides')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {element.atomicNumber === 57 ? '57-71' : '89-103'}
              </div>
            );
          }

          // Ne pas afficher les lanthanides et actinides dans le tableau principal
          if ((element.atomicNumber >= 58 && element.atomicNumber <= 71) ||
              (element.atomicNumber >= 90 && element.atomicNumber <= 103)) {
            return null;
          }

          return (
            <div
              key={element.id}
              style={{
                gridColumn: element.gridColumn,
                gridRow: element.gridRow,
                backgroundColor: hoveredElement === element.id ? `${element.color}30` : `${element.color}10`,
                borderColor: element.color,
              }}
              className="relative rounded border-2 p-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg group overflow-hidden"
              onMouseEnter={() => setHoveredElement(element.id)}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => onElementClick(element)}
            >
              {/* Sprite en fond */}
              <div
                className="absolute inset-0 transition-opacity duration-300"
                style={{
                  ...spriteStyle(element.atomicNumber),
                  opacity: hoveredElement === element.id ? 0.6 : 0.3,
                }}
              />

              {/* Numéro atomique */}
              <div className="relative z-10 absolute top-0.5 left-1 text-[10px] dark:text-[#a8b2d1] light:text-gray-600">
                {element.atomicNumber}
              </div>

              {/* Symbole */}
              <div
                className="relative z-10 text-center font-bold text-lg mt-2"
                style={{
                  color: element.color,
                  textShadow: hoveredElement === element.id && theme === 'dark' ? `0 0 10px ${element.color}80` : 'none',
                }}
              >
                {element.symbol}
              </div>

              {/* Nom */}
              <div className="relative z-10 text-center text-[9px] dark:text-[#a8b2d1] light:text-gray-700 truncate mt-0.5">
                {element.name}
              </div>

              {/* Masse atomique */}
              <div className="relative z-10 text-center text-[8px] dark:text-[#a8b2d1] light:text-gray-600 mt-0.5">
                {element.atomicWeight.toFixed(2)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Séparateur */}
      <div className="h-4" />

      {/* Lanthanides */}
      <div id="lanthanides" className="mb-4">
        <div className="dark:text-[#64ffda] light:text-teal-600 text-sm font-semibold mb-2 flex items-center gap-2">
          <span className="w-12 h-8 flex items-center justify-center dark:bg-[#112240] light:bg-gray-100 rounded border dark:border-[#64ffda] light:border-teal-600 text-xs">
            57-71
          </span>
          Lanthanides
        </div>
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: 'repeat(15, minmax(70px, 1fr))',
          }}
        >
          {lanthanides.map((element) => (
            <div
              key={element.id}
              style={{
                backgroundColor: hoveredElement === element.id ? `${element.color}30` : `${element.color}10`,
                borderColor: element.color,
              }}
              className="relative rounded border-2 p-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg overflow-hidden"
              onMouseEnter={() => setHoveredElement(element.id)}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => onElementClick(element)}
            >
              <div className="absolute inset-0 transition-opacity duration-300" style={{ ...spriteStyle(element.atomicNumber), opacity: hoveredElement === element.id ? 0.6 : 0.3 }} />
              <div className="relative z-10 absolute top-0.5 left-1 text-[10px] dark:text-[#a8b2d1] light:text-gray-600">{element.atomicNumber}</div>
              <div className="relative z-10 text-center font-bold text-lg mt-2" style={{ color: element.color, textShadow: hoveredElement === element.id && theme === 'dark' ? `0 0 10px ${element.color}80` : 'none' }}>{element.symbol}</div>
              <div className="relative z-10 text-center text-[9px] dark:text-[#a8b2d1] light:text-gray-700 truncate mt-0.5">{element.name}</div>
              <div className="relative z-10 text-center text-[8px] dark:text-[#a8b2d1] light:text-gray-600 mt-0.5">{element.atomicWeight.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Actinides */}
      <div id="actinides">
        <div className="dark:text-[#64ffda] light:text-teal-600 text-sm font-semibold mb-2 flex items-center gap-2">
          <span className="w-12 h-8 flex items-center justify-center dark:bg-[#112240] light:bg-gray-100 rounded border dark:border-[#64ffda] light:border-teal-600 text-xs">
            89-103
          </span>
          Actinides
        </div>
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: 'repeat(15, minmax(70px, 1fr))',
          }}
        >
          {actinides.map((element) => (
            <div
              key={element.id}
              style={{
                backgroundColor: hoveredElement === element.id ? `${element.color}30` : `${element.color}10`,
                borderColor: element.color,
              }}
              className="relative rounded border-2 p-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg overflow-hidden"
              onMouseEnter={() => setHoveredElement(element.id)}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => onElementClick(element)}
            >
              <div className="absolute inset-0 transition-opacity duration-300" style={{ ...spriteStyle(element.atomicNumber), opacity: hoveredElement === element.id ? 0.6 : 0.3 }} />
              <div className="relative z-10 absolute top-0.5 left-1 text-[10px] dark:text-[#a8b2d1] light:text-gray-600">{element.atomicNumber}</div>
              <div className="relative z-10 text-center font-bold text-lg mt-2" style={{ color: element.color, textShadow: hoveredElement === element.id && theme === 'dark' ? `0 0 10px ${element.color}80` : 'none' }}>{element.symbol}</div>
              <div className="relative z-10 text-center text-[9px] dark:text-[#a8b2d1] light:text-gray-700 truncate mt-0.5">{element.name}</div>
              <div className="relative z-10 text-center text-[8px] dark:text-[#a8b2d1] light:text-gray-600 mt-0.5">{element.atomicWeight.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      </div>{/* end min-w */}
      </div>{/* end overflow-x-auto */}

      {/* Légende des catégories */}
      <div className="mt-6 p-4 dark:bg-[#112240] light:bg-gray-50 rounded-lg border dark:border-[#233554] light:border-gray-200">
        <h2 className="text-sm font-semibold dark:text-[#64ffda] light:text-teal-600 mb-3">{t('categories')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
          {[
            { name: tEl('categoryAlkaliMetal'), color: '#ff5370' },
            { name: tEl('categoryAlkalineEarthMetal'), color: '#ffcb6b' },
            { name: tEl('categoryTransitionMetal'), color: '#f78c6c' },
            { name: tEl('categoryPostTransitionMetal'), color: '#89ddff' },
            { name: tEl('categoryMetalloid'), color: '#c3e88d' },
            { name: tEl('categoryNonmetal'), color: '#64ffda' },
            { name: tEl('categoryHalogen'), color: '#c792ea' },
            { name: tEl('categoryNobleGas'), color: '#82aaff' },
            { name: tEl('categoryLanthanide'), color: '#ffcb8b' },
            { name: tEl('categoryActinide'), color: '#ff6188' },
          ].map((category) => (
            <div key={category.name} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded border-2"
                style={{
                  backgroundColor: `${category.color}20`,
                  borderColor: category.color
                }}
              />
              <span className="dark:text-[#a8b2d1] light:text-gray-700">{category.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
